import type {
  ShiftAttentionItem,
  ShiftNote,
  ShiftRow,
  ShiftTileDetail,
  ShiftTileSummary,
  ShiftWindow,
} from '@/api/shiftDashboardApi'
import { cellText } from './shiftTiles'
import { noteCategoryLabel, sortNotes } from './shiftNotes'

// ─── Getting the shift out of the page (z8nrz7fbcj) ──────────────────────────
// A drill-down as CSV, and the handover as plain text to paste into a message
// or a log. Pure, so the rules are unit-tested (vitest here runs `.ts` only).

/** One CSV field: quoted when it holds a comma, quote or line break; formula-looking text neutralised. */
export function csvField(value: string): string {
  // A leading = + - @ makes a spreadsheet evaluate the cell (CSV injection).
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
}

/** The rows shown, in the columns shown, plus a Status column. CRLF line ends, as Excel expects. */
export function rowsToCsv(columns: ShiftTileDetail['columns'], rows: ShiftRow[]): string {
  const header = [...columns.map((c) => c.label), 'Status']
  const lines = rows.map((r) => [...columns.map((c) => cellText(r.cells[c.key] ?? null)), r.status])
  return [header, ...lines].map((line) => line.map(csvField).join(',')).join('\r\n')
}

/** "shift-equipment-2026-10-15.csv". */
export function csvFileName(tileId: string, date: string): string {
  return `shift-${tileId.replace(/_/g, '-')}-${date}.csv`
}

/**
 * The handover, as text a person reads top to bottom: the shift, the tiles
 * that are not fine, what needs attention, and the open notes.
 */
export function handoverText({
  window,
  tiles,
  attention,
  attentionTotal,
  notes,
}: {
  window: ShiftWindow
  tiles: ShiftTileSummary[]
  attention: ShiftAttentionItem[]
  attentionTotal: number
  notes: ShiftNote[]
}): string {
  const out: string[] = [`Shift handover: ${window.label}`, '']

  const flagged = tiles.filter((t) => t.available && (t.status === 'critical' || t.status === 'attention'))
  out.push('Needs follow-up')
  if (flagged.length === 0) out.push('- Every tile is clear.')
  for (const t of flagged) out.push(`- ${t.title}: ${t.count} (${t.statusLine})`)

  out.push('', `Attention (${attentionTotal})`)
  if (attention.length === 0) out.push('- Nothing.')
  for (const a of attention) {
    out.push(`- [${a.badge}] ${a.title}: ${a.subtitle}. Owner: ${a.owner ?? 'Unassigned'}`)
  }
  if (attentionTotal > attention.length) out.push(`- …and ${attentionTotal - attention.length} more on the dashboard.`)

  const open = sortNotes(notes).filter((n) => !n.resolved)
  out.push('', `Open notes (${open.length})`)
  if (open.length === 0) out.push('- None.')
  for (const n of open) out.push(`- ${noteCategoryLabel(n.category)}: ${n.text.replace(/\s*\n\s*/g, ' ')} (${n.createdBy})`)

  return out.join('\n')
}
